import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryService } from '../services/inventoryService';

const BulkReconcilePage = () => {
  const navigate = useNavigate();
  
  // Available items and search
  const [availableItems, setAvailableItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Reconciliation list (items added by user)
  const [reconcileList, setReconcileList] = useState([]); // [{ item, physical_count, notes }]
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      fetchAvailableItems(searchTerm);
    } else {
      setAvailableItems([]);
      setShowDropdown(false);
    }
  }, [searchTerm]);

  const fetchAvailableItems = async (search) => {
    setLoading(true);
    try {
      const result = await inventoryService.getSummary(0, 100, search);
      setAvailableItems(result.data);
      setShowDropdown(true);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  // Filter items for search dropdown (exclude already added items)
  // Check if an item has a price for reconciliation
  const hasPrice = (item) => {
    return (item.average_unit_price && item.average_unit_price > 0) || 
           (item.default_price && item.default_price > 0);
  };

  const searchResults = availableItems
    .filter(item => {
      const alreadyAdded = reconcileList.some(r => r.item.item_id === item.item_id);
      return !alreadyAdded;
    })
    .slice(0, 10);

  const addItemToList = (item) => {
    setReconcileList(prev => [...prev, { 
      item, 
      physical_count: item.total_quantity, // Default to current system quantity
      notes: '' 
    }]);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const removeFromList = (itemId) => {
    setReconcileList(prev => prev.filter(r => r.item.item_id !== itemId));
  };

  const updatePhysicalCount = (itemId, value) => {
    const count = parseInt(value, 10);
    if (!isNaN(count) && count >= 0) {
      setReconcileList(prev => prev.map(r => 
        r.item.item_id === itemId ? { ...r, physical_count: count } : r
      ));
    }
  };

  const updateNotes = (itemId, notes) => {
    setReconcileList(prev => prev.map(r => 
      r.item.item_id === itemId ? { ...r, notes } : r
    ));
  };

  const calculateDifference = (systemQty, physicalCount) => {
    const diff = physicalCount - systemQty;
    return diff;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (reconcileList.length === 0) {
      alert('Please add at least one item to reconcile');
      return;
    }

    // Filter out items with no difference
    const itemsToAdjust = reconcileList.filter(r => 
      calculateDifference(r.item.total_quantity, r.physical_count) !== 0
    );

    if (itemsToAdjust.length === 0) {
      alert('No adjustments needed - all physical counts match system quantities');
      return;
    }

    // Check for items without prices
    const itemsWithoutPrice = itemsToAdjust.filter(r => !hasPrice(r.item));
    if (itemsWithoutPrice.length > 0) {
      const itemNames = itemsWithoutPrice.map(r => r.item.item_name).join(', ');
      alert(`Cannot reconcile items without a price. Please set a default price for: ${itemNames}`);
      return;
    }

    if (!window.confirm(`This will adjust ${itemsToAdjust.length} items. Continue?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const adjustments = itemsToAdjust.map(({ item, physical_count, notes }) => ({
        item_id: item.item_id,
        physical_count,
        notes: notes || 'Bulk reconciliation'
      }));

      const response = await inventoryService.bulkReconcile(adjustments);
      
      alert(`Successfully reconciled ${response.adjustments_made} items`);
      navigate('/inventory/summary');
    } catch (err) {
      console.error('Error reconciling inventory:', err);
      setError(err.response?.data?.detail || 'Failed to reconcile inventory');
    } finally {
      setLoading(false);
    }
  };

  const totalItems = reconcileList.length;
  const itemsWithDifferences = reconcileList.filter(r => 
    calculateDifference(r.item.total_quantity, r.physical_count) !== 0
  ).length;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-clipboard-check me-2"></i>
          Bulk Reconciliation
        </h2>
        <button className="btn btn-secondary" onClick={() => navigate('/inventory/summary')}>
          <i className="bi bi-arrow-left me-2"></i>
          Back
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Search and Add Items */}
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Add Items to Reconcile</h5>
            <div className="row">
              <div className="col-md-8">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search for items to reconcile..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                  />
                  {showDropdown && searchTerm && searchResults.length > 0 && (
                    <div 
                      className="dropdown-menu show w-100" 
                      style={{ maxHeight: '300px', overflowY: 'auto', position: 'absolute', zIndex: 1000 }}
                    >
                      {searchResults.map(item => (
                        <button
                          key={item.item_id}
                          type="button"
                          className="dropdown-item"
                          onClick={() => addItemToList(item)}
                        >
                          <div>
                            <strong>{item.item_name}</strong>
                            {!hasPrice(item) && (
                              <span className="badge bg-warning text-dark ms-2" title="No price set - cannot reconcile">
                                <i className="bi bi-exclamation-triangle"></i> No Price
                              </span>
                            )}
                            <br />
                            <small className="text-muted">
                              {item.category_name} | Current: {item.total_quantity} {item.unit_of_measure}
                            </small>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showDropdown && searchTerm && searchResults.length === 0 && (
                    <div className="dropdown-menu show w-100">
                      <div className="dropdown-item-text text-muted">
                        No items found
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reconciliation List */}
        {reconcileList.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">
                Items to Reconcile ({totalItems} items, {itemsWithDifferences} with differences)
              </h5>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '25%' }}>Item</th>
                      <th style={{ width: '15%' }}>Category</th>
                      <th className="text-end" style={{ width: '12%' }}>System Qty</th>
                      <th className="text-end" style={{ width: '12%' }}>Physical Count</th>
                      <th className="text-end" style={{ width: '12%' }}>Difference</th>
                      <th style={{ width: '20%' }}>Notes</th>
                      <th style={{ width: '4%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconcileList.map((r) => {
                      const difference = calculateDifference(r.item.total_quantity, r.physical_count);
                      const diffClass = difference > 0 ? 'text-success' : difference < 0 ? 'text-danger' : 'text-muted';
                      const itemHasPrice = hasPrice(r.item);
                      
                      return (
                        <tr key={r.item.item_id} className={!itemHasPrice && difference !== 0 ? 'table-warning' : ''}>
                          <td>
                            <strong>{r.item.item_name}</strong>
                            {!itemHasPrice && difference !== 0 && (
                              <div className="mt-1">
                                <span className="badge bg-warning text-dark">
                                  <i className="bi bi-exclamation-triangle"></i> No price - cannot reconcile
                                </span>
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="badge bg-info">{r.item.category_name}</span>
                          </td>
                          <td className="text-end">
                            {r.item.total_quantity} {r.item.unit_of_measure}
                          </td>
                          <td className="text-end">
                            <input
                              type="number"
                              className="form-control form-control-sm text-end"
                              value={r.physical_count}
                              onChange={(e) => updatePhysicalCount(r.item.item_id, e.target.value)}
                              min="0"
                              step="1"
                              required
                            />
                          </td>
                          <td className={`text-end fw-bold ${diffClass}`}>
                            {difference > 0 && '+'}
                            {difference !== 0 ? `${difference} ${r.item.unit_of_measure}` : '—'}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Optional notes..."
                              value={r.notes}
                              onChange={(e) => updateNotes(r.item.item_id, e.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeFromList(r.item.item_id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        {reconcileList.length > 0 && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">Ready to Submit</h5>
                  <p className="text-muted mb-0">
                    {itemsWithDifferences > 0 
                      ? `${itemsWithDifferences} item(s) will be adjusted`
                      : 'No adjustments needed - all counts match'
                    }
                  </p>
                </div>
                <button 
                  type="submit" 
                  className="btn btn-primary btn-lg"
                  disabled={loading || itemsWithDifferences === 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Reconcile Inventory
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {reconcileList.length === 0 && !loading && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bi bi-clipboard-check text-muted" style={{ fontSize: '4rem' }}></i>
              <h5 className="mt-3 text-muted">No Items Added Yet</h5>
              <p className="text-muted">Search and add items above to start reconciling inventory</p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default BulkReconcilePage;
